// Contenu du portfolio. Remplacer les visuels provisoires (.svg) par les vrais fichiers
// dans public/portfolio/<slug>/ puis mettre à jour les chemins ici (src, w, h).
// Les textes sont bilingues : { fr, en }.

export type Lang = 'fr' | 'en';
export type T = Record<Lang, string>;
export type Category = 'packaging' | 'identity' | 'social' | 'web' | 'illustration';
// Effet de survol du visuel principal, choisi selon le métier montré (voir ShaderImage).
export type Effect = 'print' | 'emboss' | 'swipe' | 'flow';
export type Img = { src: string; w: number; h: number; alt: string; caption?: T; bottle?: { src: string; w: number; h: number } };
export type Stat = { value: number | null; prefix?: string; suffix?: string; label: T };
// Post ou story Instagram. Les stories n'ont pas d'URL permanente.
export type Post = Img & { caption: T; date?: string; quote?: string; url?: string };

export type Project = {
    slug: string;
    title: string;
    client: string;
    place: T;
    year: string;
    category: Category;
    role: T;
    deliverables: T;
    summary: T;
    context: T;
    brief: T;
    process: T;
    result: T;
    cover: Img;
    gallery: Img[];
    video?: string;
    stats?: Stat[];
    link?: string;
    materials?: Img[];
};

export const CONTACT = {
    email: 'bonjour@gauthier-rodriguez.fr',
    cv: '/cv-gauthier-rodriguez.pdf', // TODO: déposer le PDF dans public/
    instagram: 'https://www.instagram.com/nonspeculativeart',
    city: 'Toulouse',
};

export const categories: Record<Category, T> = {
    packaging: { fr: 'Packaging', en: 'Packaging' },
    identity: { fr: 'Identité visuelle', en: 'Visual identity' },
    social: { fr: 'Réseaux sociaux', en: 'Social media' },
    web: { fr: 'Web', en: 'Web' },
    illustration: { fr: 'Illustration', en: 'Illustration' },
};

export const effects: Record<Category, Effect> = {
    packaging: 'print', // trame d'impression CMJN
    illustration: 'print',
    identity: 'emboss', // gaufrage sous lumière rasante
    social: 'swipe', // flou de mouvement d'un fil qu'on fait défiler
    web: 'flow', // ondulation liquide qui suit le curseur
};

// Les 7 étiquettes Casademont. Sources (PDF/PNG HD) dans /sources, hors du site public ;
// ici des WebP ≤ 2000 px générés depuis ces sources.
const label = (file: string, name: string, w: number, h: number, caption?: T): Img => ({
    src: `/portfolio/casademont/${file}.webp`,
    w,
    h,
    alt: `Étiquette ${name}, Domaine Casademont`,
    caption: caption ?? { fr: name, en: name },
});

const casademontLabels: Img[] = [
    label('cremat', 'Crémat', 1415, 2000, {
        fr: 'Crémat — à la carte du Meteora, restaurant étoilé Michelin à Los Angeles',
        en: 'Crémat — on the list at Meteora, a Michelin-starred restaurant in Los Angeles',
    }),
    label('florange', 'Florange', 1200, 900, {
        fr: 'Florange — à la carte du Butcher’s Daughter, Venice',
        en: 'Florange — on the list at The Butcher’s Daughter, Venice',
    }),
    label('contact', 'Contact', 2000, 1545),
    label('got-de-blanc', 'Got de Blanc', 1455, 990),
    label('got-de-vi', 'Got de Vì', 1455, 990),
    label('glounache', 'Glounache', 2000, 1500, { fr: 'Glounache — lettrage', en: 'Glounache — lettering' }),
    label('estalat', 'Estalat', 1500, 2000),
];

// Mockups de bouteilles (sources/portfolio/casademont/mockups, recadrés en WebP) :
// la tuile de la gamme bascule de l'étiquette à la bouteille au survol.
const bottles: Record<string, { w: number; h: number }> = {
    cremat: { w: 233, h: 959 },
    florange: { w: 273, h: 961 },
    glounache: { w: 272, h: 961 },
    'got-de-blanc': { w: 270, h: 960 },
    'got-de-vi': { w: 271, h: 957 },
};
for (const l of casademontLabels) {
    const file = l.src.split('/').pop()!.replace('.webp', '');
    if (bottles[file]) l.bottle = { src: `/portfolio/casademont/bouteilles/${file}.webp`, ...bottles[file] };
}

// Posts et stories conçus pour @domaine_casademont (visuels, légendes, stratégie, prises de vue).
// Originaux dans /sources/portfolio/social.
export const posts: Post[] = [
    {
        src: '/portfolio/social/post-raoul.webp', w: 586, h: 1760,
        alt: 'Post Instagram : photo d’archive de Raoul Casademont dans ses vignes, avec la légende',
        caption: { fr: 'Storytelling de marque', en: 'Brand storytelling' },
        date: '2025-12-15',
        quote: 'They say you can’t go home again... but we bought the vineyard back.',
        url: 'https://www.instagram.com/domaine_casademont/p/DSSlg87DWRz/',
    },
    {
        src: '/portfolio/social/story-farmshop.webp', w: 901, h: 1600,
        alt: 'Story Instagram : le Florange arrive chez Farmshop à Santa Monica',
        caption: { fr: 'Story : arrivée chez Farmshop, Santa Monica', en: 'Story: now at Farmshop, Santa Monica' },
    },
    {
        src: '/portfolio/social/story-butchers-daughter.webp', w: 900, h: 1600,
        alt: 'Story Instagram : le Florange au Butcher’s Daughter de Venice',
        caption: { fr: 'Story : nouvelle adresse à Venice', en: 'Story: new spot in Venice' },
        url: 'https://www.instagram.com/domaine_casademont/p/DXfhSu0gqbL/',
    },
    {
        src: '/portfolio/social/story-butchers-daughter-carte.webp', w: 1080, h: 1350,
        alt: 'Story Instagram : la carte du Butcher’s Daughter annotée « That’s us »',
        caption: { fr: 'Story : la carte annotée à la main', en: 'Story: the hand-annotated wine list' },
    },
    {
        src: '/portfolio/social/story-valentina.webp', w: 739, h: 1600,
        alt: 'Story Instagram : le Glounache à la carte de Valentina',
        caption: { fr: 'Story : le Glounache chez Valentina', en: 'Story: Glounache at Valentina' },
    },
    {
        src: '/portfolio/social/post-anajak.webp', w: 586, h: 1760,
        alt: 'Post Instagram : le Brisat à la carte d’Anajak Thai, Los Angeles, avec la légende',
        caption: { fr: 'Annonce de distribution', en: 'Distribution news' },
        quote: 'We are incredibly honored to announce that our 2024 Brisat is now pouring at Anajak Thai…',
    },
];

export const projects: Project[] = [
    {
        slug: 'casademont',
        title: 'Casademont',
        client: 'Domaine Casademont',
        place: { fr: 'Languedoc → Los Angeles', en: 'Languedoc → Los Angeles' },
        year: '2024–2026', // TODO
        category: 'packaging',
        role: { fr: 'Illustration, direction artistique, mise en page, supports de vente', en: 'Illustration, art direction, layout, sales materials' },
        deliverables: {
            fr: '7 étiquettes, présentation commerciale, fiches techniques',
            en: '7 labels, sales deck, tech sheets',
        },
        summary: {
            fr: 'Sept étiquettes pour un domaine de vins nature. L’une d’elles, le Crémat, est à la carte du Meteora, restaurant étoilé Michelin de Los Angeles.',
            en: 'Seven labels for a natural wine estate. One of them, Crémat, is on the list at Meteora, a Michelin-starred restaurant in Los Angeles.',
        },
        context: {
            fr: 'Le Domaine Casademont fait revivre les vignes de Raoul Casademont, rachetées par la famille en 2024, et produit des vins nature vendus entre la France et la Californie.',
            en: 'Domaine Casademont revives the vines of Raoul Casademont, bought back by the family in 2024, and makes natural wines sold between France and California.',
        },
        brief: {
            fr: 'Donner à chaque cuvée sa propre personnalité tout en gardant une famille reconnaissable, assez forte pour tenir sur une table étoilée et sur un écran de téléphone.',
            en: 'Give each wine its own personality while keeping a recognisable family, strong enough for a fine-dining table and a phone screen.',
        },
        process: {
            fr: 'Dessin au trait, choix typographiques, tests de papiers et de couleurs, puis allers-retours avec le domaine jusqu’au bon à tirer de chaque cuvée.',
            en: 'Line drawing, type exploration, paper and colour tests, then rounds with the estate up to print approval for each wine.',
        },
        result: {
            fr: 'Une gamme de sept étiquettes. Le Crémat est servi au Meteora, restaurant étoilé Michelin de Los Angeles, le Florange au Butcher’s Daughter de Venice. Pour accompagner la vente, j’ai aussi conçu la présentation commerciale du domaine et les fiches techniques des cuvées.',
            en: 'A range of seven labels. Crémat is poured at Meteora, a Michelin-starred restaurant in Los Angeles, Florange at The Butcher’s Daughter in Venice. To support sales, I also designed the estate’s sales deck and tech sheets.',
        },
        cover: casademontLabels[0],
        gallery: casademontLabels.slice(1),
        materials: [
            {
                src: '/portfolio/casademont/supports/presentation.webp', w: 1536, h: 1024,
                alt: 'Mockup de la présentation commerciale du Domaine Casademont, six pages',
                caption: { fr: 'Présentation commerciale du domaine', en: 'Estate sales deck' },
            },
            {
                src: '/portfolio/casademont/supports/tech-sheets.webp', w: 1536, h: 1024,
                alt: 'Mockup des fiches techniques Crémat et Florange',
                caption: { fr: 'Fiches techniques', en: 'Tech sheets' },
            },
            {
                src: '/portfolio/casademont/supports/tech-sheet-cremat.webp', w: 2000, h: 1125,
                alt: 'Fiche technique du Crémat',
                caption: { fr: 'Fiche technique — Crémat', en: 'Tech sheet — Crémat' },
            },
            {
                src: '/portfolio/casademont/supports/tech-sheet-florange.webp', w: 2000, h: 1125,
                alt: 'Fiche technique du Florange',
                caption: { fr: 'Fiche technique — Florange', en: 'Tech sheet — Florange' },
            },
        ],
        link: 'https://domainecasademont.com',
    },
    {
        slug: 'gran-blanco',
        title: 'Gran Blanco',
        client: 'Gran Blanco × Casademont',
        place: { fr: 'Venice Beach, Californie', en: 'Venice Beach, California' },
        year: '2025', // TODO
        category: 'packaging',
        role: { fr: 'Illustration, design d’étiquette', en: 'Illustration, label design' },
        deliverables: { fr: 'Étiquette de cuvée maison', en: 'House wine label' },
        summary: {
            fr: 'La cuvée maison du bar-restaurant Gran Blanco à Venice Beach, produite par le Domaine Casademont.',
            en: 'The house wine of Gran Blanco, a Venice Beach bar & restaurant, made by Domaine Casademont.',
        },
        context: {
            fr: 'Gran Blanco, bar-restaurant de Venice Beach, voulait sa propre cuvée produite par le Domaine Casademont.',
            en: 'Gran Blanco, a Venice Beach bar & restaurant, wanted its own wine made by Domaine Casademont.',
        },
        brief: {
            fr: 'Mêler l’énergie de Venice Beach à l’héritage du domaine, sur une étiquette qui fonctionne au comptoir comme sur Instagram.',
            en: 'Blend Venice Beach energy with the estate’s heritage, on a label that works at the bar and on Instagram.',
        },
        process: {
            fr: 'Moodboard Californie, dessin au trait, déclinaisons de couleurs validées avec le restaurant et le domaine.',
            en: 'California moodboard, line drawing, colour variations approved with the restaurant and the estate.',
        },
        result: {
            fr: 'L’étiquette habille la cuvée maison servie chez Gran Blanco.',
            en: 'The label dresses the house wine served at Gran Blanco.',
        },
        cover: { src: '/portfolio/gran-blanco/gran-blanco.webp', w: 1500, h: 2000, alt: 'Étiquette Gran Blanco : la façade du restaurant dessinée au trait' },
        gallery: [],
    },
    {
        // TODO: résultat (les sections vides ne s'affichent pas).
        slug: 'jos-wines',
        title: 'Jos Wines',
        client: 'Jos Wines',
        place: { fr: 'Géorgie → Californie', en: 'Georgia → California' },
        year: '2023–2024',
        category: 'packaging',
        role: { fr: 'Illustration, design d’étiquette', en: 'Illustration, label design' },
        deliverables: { fr: '7 étiquettes', en: '7 labels' },
        summary: {
            fr: 'Sept étiquettes illustrées pour Jos Wines, une marque de vins géorgiens vendus en Californie.',
            en: 'Seven illustrated labels for Jos Wines, a brand of Georgian wines sold in California.',
        },
        context: {
            fr: 'Jos Wines faisait son vin en Géorgie, le pays où sont nés les vins orange, et le vendait aux États-Unis, surtout en Californie.',
            en: 'Jos Wines made its wine in Georgia, the birthplace of orange wine, and sold it in the United States, mostly in California.',
        },
        // Brief et process rédigés sans source (à la demande de Gauthier) : à relire avant envoi.
        brief: {
            fr: 'Faire remarquer des vins géorgiens, encore peu connus aux États-Unis, sur les étagères des cavistes californiens. Chaque cuvée devait avoir son propre visuel, coloré et facile à retenir, sans codes de vin classique.',
            en: 'Get Georgian wines, still little known in the US, noticed on Californian wine shop shelves. Each wine needed its own visual, colourful and easy to remember, away from classic wine codes.',
        },
        process: {
            fr: 'Une technique par cuvée : lettrage en photos de fleurs, peinture numérique, dessin d’un seul trait, aquarelle. Puis le choix des couleurs, la mise en page de l’étiquette et les ajustements avec la marque jusqu’à l’impression.',
            en: 'One technique per wine: lettering made of flower photos, digital painting, single-line drawing, watercolour. Then colour choices, label layout and rounds with the brand up to print.',
        },
        result: { fr: '', en: '' },
        cover: { src: '/portfolio/jos-wines/spring-flower.webp', w: 640, h: 480, alt: 'Étiquette Spring Flower : lettrage fleuri' },
        gallery: [
            { src: '/portfolio/jos-wines/summer-flower.webp', w: 2000, h: 1501, alt: 'Étiquette Summer Flower : lettrage composé de photos de fleurs' },
            { src: '/portfolio/jos-wines/raisin-violet.webp', w: 640, h: 384, alt: 'Étiquette : raisin violet tenu à deux mains' },
            { src: '/portfolio/jos-wines/raisin-dore.webp', w: 640, h: 384, alt: 'Étiquette : raisin doré tenu à deux mains, éclaboussures de couleur' },
            { src: '/portfolio/jos-wines/elephant.webp', w: 640, h: 384, alt: 'Étiquette : éléphant dessiné d’un seul trait' },
            { src: '/portfolio/jos-wines/girafe.webp', w: 640, h: 480, alt: 'Étiquette : girafe aux couleurs d’aquarelle orange' },
            { src: '/portfolio/jos-wines/flash-orange.webp', w: 640, h: 384, alt: 'Étiquette Flash Orange : bouteille et éclaboussure de jus' },
        ],
    },
    {
        slug: 'leslie-pons',
        title: 'Leslie Pons Avocat',
        client: 'Me Leslie Pons, avocate',
        place: { fr: 'Paris', en: 'Paris' },
        year: '2025', // TODO
        category: 'identity',
        role: { fr: 'Logo, identité visuelle, papeterie, site web', en: 'Logo, visual identity, stationery, website' },
        deliverables: { fr: 'Monogramme, cartes de visite, site vitrine', en: 'Monogram, business cards, website' },
        summary: {
            fr: 'Un monogramme LP en une seule boucle pour une avocate parisienne, décliné du papier à l’écran.',
            en: 'A single-loop LP monogram for a Paris lawyer, carried from paper to screen.',
        },
        context: {
            fr: 'Leslie Pons est avocate à la Cour, spécialisée en droit immobilier et baux commerciaux, avenue Kléber à Paris.',
            en: 'Leslie Pons is a Paris lawyer specialising in real estate and commercial lease law, based on avenue Kléber.',
        },
        brief: {
            fr: 'Une identité sobre et élégante qui inspire confiance, loin des codes austères des cabinets d’avocats.',
            en: 'A calm, elegant identity that builds trust, away from the stiff codes of law firms.',
        },
        process: {
            fr: 'Recherches de monogramme, choix d’un vert profond et d’une capitale fine à empattements, cartes de visite, puis intégration de l’identité dans le site.',
            en: 'Monogram sketches, a deep green and a fine serif capital, business cards, then the identity carried into the website.',
        },
        result: {
            fr: 'Une identité cohérente, des cartes de visite au site lesliepons-avocat.fr.',
            en: 'A consistent identity, from business cards to the lesliepons-avocat.fr website.',
        },
        cover: { src: '/portfolio/leslie-pons/cartes-de-visite.webp', w: 1536, h: 1024, alt: 'Cartes de visite de Me Leslie Pons, recto et verso, vert profond et monogramme LP' },
        gallery: [{ src: '/portfolio/sites-web/leslie-pons-accueil.jpg', w: 1440, h: 900, alt: 'Accueil du site de Me Leslie Pons' }],
        link: 'https://lesliepons-avocat.fr',
    },
    {
        slug: 'social-media',
        title: 'Social Media',
        client: '@domaine_casademont',
        place: { fr: 'Instagram', en: 'Instagram' },
        year: '2025–2026',
        category: 'social',
        role: { fr: 'Stratégie, visuels et montages, légendes, photo et vidéo', en: 'Strategy, visuals and edits, captions, photo and video' },
        deliverables: { fr: 'Posts, reels, calendrier éditorial', en: 'Posts, reels, content calendar' },
        summary: {
            fr: 'Le compte Instagram du Domaine Casademont, de la stratégie à la prise de vue.',
            en: 'Domaine Casademont’s Instagram, from strategy to shooting.',
        },
        context: {
            fr: 'Un jeune domaine qui vend à des restaurants et cavistes de Los Angeles et doit exister en ligne face à des maisons bien plus grandes.',
            en: 'A young estate selling to Los Angeles restaurants and wine shops, competing online with much bigger houses.',
        },
        brief: {
            fr: 'Raconter l’histoire de la famille, annoncer chaque nouvelle cuvée et chaque nouvelle carte, en français et en anglais.',
            en: 'Tell the family story, announce every new wine and every new list, in French and English.',
        },
        process: {
            fr: 'Ligne éditoriale, calendrier, prises de vue au domaine, montage des reels, rédaction des légendes, puis lecture des statistiques.',
            en: 'Editorial line, calendar, shoots at the estate, reel editing, captions, then reading the analytics.',
        },
        result: {
            fr: 'Un compte qui relaie chaque arrivée sur une carte, du Meteora au Butcher’s Daughter.',
            en: 'An account that amplifies every new listing, from Meteora to The Butcher’s Daughter.',
        },
        cover: posts[1],
        gallery: [
            ...posts.filter((_, i) => i !== 1),
            {
                src: '/portfolio/social/recap-mars.webp', w: 942, h: 1600,
                alt: 'Bilan Instagram de mars : 1K vues, 33 % de non-abonnés, 225 abonnés',
                caption: { fr: 'Bilan Instagram de mars', en: 'March Instagram recap' },
            },
        ],
        // Bilan Instagram de mars (capture dans la galerie). TODO: préciser l'année si besoin.
        stats: [
            { value: 38, prefix: '+', suffix: '%', label: { fr: 'de vues en mars par rapport à février', en: 'views in March vs February' } },
            { value: 33, suffix: '%', label: { fr: 'des vues venant de non-abonnés', en: 'of views from non-followers' } },
            { value: 17, prefix: '+', label: { fr: 'abonnés en un mois', en: 'followers in one month' } },
        ],
        link: 'https://www.instagram.com/domaine_casademont/',
    },
    {
        slug: 'sites-web',
        title: 'Sites web',
        client: 'Domaine Casademont · Me Leslie Pons',
        place: { fr: 'France / Californie', en: 'France / California' },
        year: '2025',
        category: 'web',
        role: { fr: 'Design et développement', en: 'Design & development' },
        deliverables: { fr: 'Sites vitrines responsive', en: 'Responsive websites' },
        summary: {
            fr: 'Des sites qui prolongent l’identité visuelle jusqu’à l’écran.',
            en: 'Websites that carry the visual identity through to the screen.',
        },
        context: {
            fr: 'Après les étiquettes, le Domaine Casademont avait besoin d’un site à la hauteur. Même démarche pour un cabinet d’avocate parisien.',
            en: 'After the labels, Domaine Casademont needed a website to match. Same approach for a Paris law firm.',
        },
        brief: {
            fr: 'Raconter l’histoire du domaine en images et rendre l’offre du cabinet lisible en quelques secondes.',
            en: 'Tell the estate’s story through images and make the firm’s offer clear in seconds.',
        },
        process: {
            fr: 'Maquettes, intégration Next.js, animations, optimisation mobile et SEO.',
            en: 'Mockups, Next.js build, animations, mobile and SEO optimisation.',
        },
        result: {
            fr: 'Deux sites en ligne, cohérents avec l’identité de chaque client.',
            en: 'Two live websites, consistent with each client’s identity.',
        },
        cover: { src: '/portfolio/sites-web/casademont-cuvees.jpg', w: 1440, h: 900, alt: 'Section Cuvées du site Domaine Casademont : la gamme de bouteilles alignées' },
        gallery: [
            { src: '/portfolio/sites-web/casademont-accueil-en.jpg', w: 1440, h: 900, alt: 'Accueil du site Domaine Casademont, version anglaise' },
            { src: '/portfolio/sites-web/casademont-estate.jpg', w: 1440, h: 900, alt: 'Page The Estate du site Domaine Casademont : Our Story et Pépé Raoul' },
            { src: '/portfolio/sites-web/leslie-pons-accueil.jpg', w: 1440, h: 900, alt: 'Accueil du site du cabinet de Me Leslie Pons' },
        ],
        video: '/assets/videos/edit-cdm.mp4',
        link: 'https://domainecasademont.com',
    },
];

// Illustrations personnelles, tirées de @nonspeculativeart.
const playgroundItems: [string, number, number, string, T][] = [
    ['illu-7.webp', 640, 640, 'Portrait peint de Basquiat', { fr: 'Basquiat', en: 'Basquiat' }],
    ['illu-8.jpg', 1440, 1440, 'Portrait peint d’Andy Warhol sur fond noir', { fr: 'Andy Warhol', en: 'Andy Warhol' }],
    ['illu-9.webp', 640, 497, 'Portrait peint inspiré de Fear and Loathing in Las Vegas, chapeau et lunettes orange', { fr: 'Fear and Loathing in Las Vegas', en: 'Fear and Loathing in Las Vegas' }],
    ['illu-91.webp', 640, 381, 'Gremlins : Gizmo peint sur fond jaune', { fr: 'Gizmo', en: 'Gizmo' }],
    ['illu-10.jpg', 1440, 1441, 'Portrait peint de Mia Wallace, Pulp Fiction', { fr: 'Mia Wallace', en: 'Mia Wallace' }],
    ['illu-12.jpg', 1440, 1440, 'Portrait peint de Bob Dylan sur fond orange', { fr: 'Bob Dylan', en: 'Bob Dylan' }],
    ['illu-13.jpg', 1440, 1074, 'Baby Yoda peint', { fr: 'Baby Yoda', en: 'Baby Yoda' }],
    ['illu-14.jpg', 1875, 2500, 'Rosé : rose pixelisée et lettrage rose sur fond noir', { fr: 'Rosé', en: 'Rosé' }],
];
export const playground: Img[] = playgroundItems.map(([file, w, h, alt, caption]) => ({ src: `/portfolio/playground/${file}`, w, h, alt, caption }));

export const clients = ['Domaine Casademont', 'Meteora', 'Gran Blanco', 'The Butcher’s Daughter', 'Los Angeles', 'Venice Beach', 'Toulouse'];

export const ui = {
    role: { fr: 'Graphiste & Social Media', en: 'Graphic Designer & Social Media' },
    available: { fr: 'Disponible', en: 'Available' },
    intro: {
        fr: 'Je dessine des étiquettes et des identités visuelles, et je crée aussi les posts Instagram qui vont avec. Pour le Domaine Casademont, j’ai dessiné sept étiquettes et leurs supports de vente, et j’anime leur Instagram : l’une de ces bouteilles est aujourd’hui à la carte du Meteora, restaurant étoilé Michelin de Los Angeles. Si vous avez un projet, écrivez-moi.',
        en: 'I design labels and visual identities, and I also make the Instagram posts that go with them. For Domaine Casademont I drew seven labels and their sales materials, and I run their Instagram: one of those bottles is now on the list at Meteora, a Michelin-starred restaurant in Los Angeles. If you have a project, get in touch.',
    },
    cv: { fr: 'Télécharger le CV', en: 'Download CV' },
    work: { fr: 'Projets sélectionnés', en: 'Selected work' },
    all: { fr: 'Tout', en: 'All' },
    social: { fr: 'Instagram, de la stratégie à la photo', en: 'Instagram, from strategy to shoot' },
    socialIntro: {
        fr: 'Pour @domaine_casademont, je conçois les visuels et les reels, j’écris les légendes, je planifie et je photographie le domaine.',
        en: 'For @domaine_casademont I design the posts and reels, write the captions, plan the calendar and shoot the estate.',
    },
    seePost: { fr: 'Voir le post', en: 'View post' },
    playground: { fr: 'Playground', en: 'Playground' },
    playgroundIntro: { fr: 'Illustrations personnelles, sans brief.', en: 'Personal illustrations, no brief.' },
    contact: { fr: 'Travaillons ensemble', en: 'Let’s work together' },
    close: { fr: 'Fermer', en: 'Close' },
    range: { fr: 'La gamme', en: 'The range' },
    onBottle: { fr: 'Sur bouteille', en: 'On the bottle' },
    materials: { fr: 'Supports de vente', en: 'Sales materials' },
    client: { fr: 'Client', en: 'Client' },
    place: { fr: 'Lieu', en: 'Location' },
    year: { fr: 'Année', en: 'Year' },
    roleLabel: { fr: 'Rôle', en: 'Role' },
    deliverables: { fr: 'Livrables', en: 'Deliverables' },
    context: { fr: 'Contexte', en: 'Context' },
    brief: { fr: 'Brief', en: 'Brief' },
    process: { fr: 'Process', en: 'Process' },
    result: { fr: 'Résultat', en: 'Result' },
    visit: { fr: 'Voir le site', en: 'Visit site' },
    visitAccount: { fr: 'Voir le compte Instagram', en: 'View the Instagram account' },
    next: { fr: 'Projet suivant', en: 'Next project' },
    caseStudy: { fr: 'Voir l’étude de cas', en: 'View case study' },
    back: { fr: 'Tous les projets', en: 'All work' },
} satisfies Record<string, T>;
